<template>
  <div v-if="redirectCheckDataHasLoaded && redirectChecks === false">
    <div class="text-left">
      <div
        class="mt-4 sm:mt-2 sm:flex sm:gap-1"
        :class="{ 'mb-8': !shouldEnableCHRFlow }"
      >
        <div>
          <h4 class="text-dark-gray">How do you want to get paid?</h4>
          <span
            v-if="shouldEnableCHRFlow"
            class="text-sm font-normal text-neutral-subtlest"
            >Choose from the following {{ payment_terms?.length }} options</span
          >
        </div>
      </div>
      <div
        v-if="isLoading"
        class="py-2"
      >
        <img
          class="-top-2px loading relative h-8 w-full"
          src="@/assets/loading-v2-yellow.svg"
          alt=""
        />
      </div>
      <div v-else-if="payment_terms?.length === 0">
        <p class="text-red-500">
          This broker has no payment terms. Please contact the administrator.
        </p>
      </div>
      <div
        v-else
        class="mt-4 flex flex-col"
      >
        <section
          v-if="shouldEnableCHRFlow"
          class="mb-3 flex flex-col gap-3"
        >
          <p>Fastest Payment Option</p>
          <round-radio-button-terms
            :item="factoringDelta?.name"
            name="factoringDelta"
            :value="factoringDelta?.id"
            :checked="paymentTermId === factoringDelta?.id"
            :term="factoringDelta?.is_type"
            :subtext="feesToText(factoringDelta)"
            :highlight-box="true"
            highlight-text="Recommended"
            subtext-class="md:font-light radio-btn-subtitle"
            :handle-click="() => handleClick(factoringDelta)"
          />
          <p class="mt-3 text-neutral-subtle">
            {{ shouldEnableCHRFlow ? 'Other Payment Options' : 'Standard' }}
          </p>
        </section>
        <div
          v-for="payment_term in sortedPaymentTerms"
          :key="payment_term?.id"
        >
          <round-radio-button-terms
            :item="payment_term?.name"
            name="payment_terms"
            :value="payment_term?.id"
            :checked="paymentTermId === payment_term?.id"
            :term="payment_term?.is_type"
            class="mb-3"
            :subtext="feesToText(payment_term)"
            subtext-class="md:font-light radio-btn-subtitle"
            :handle-click="() => handleClick(payment_term)"
            :apm="
              approvedPaymentMethods(
                payment_term?.allowed_payment_methods,
                payment_term?.is_type
              )
            "
          />
        </div>
        <section
          v-if="shouldEnableCHRFlow"
          class="font-normal"
        >
          <p class="text-1xs text-neutral-subtle">*Subject to underwriting</p>
          <p class="text-1xs text-yellow-500">Privacy Policy</p>
          <p class="text-1xs text-neutral-subtle">
            Factoring products and services are offered by TBK Bank, SSB d/b/a
            Factoring Powered by Delta LoadPay is a product offering of TBK
            Bank, SSB, Member FDIC. The LoadPay debit card is issued by TBK
            Bank, SSB pursuant to a license from Mastercard.
          </p>
        </section>
      </div>
    </div>
    <form-navigation-buttons
      v-if="!isNavigationDisabled"
      :enabled="!isNavigationDisabled"
      :component-identifier="componentIdentifier"
      :buttons-to-display="buttonsToDisplay"
      :is-button-disabled="isButtonDisabled"
      @next="handleNext"
      @back="handleBack"
    />
  </div>
  <div
    v-else
    class="my-8"
  >
    <div
      class="flex flex-col items-center justify-center gap-3 rounded-md bg-off-white py-16"
    >
      <img
        class="-top-2px loading relative h-8 w-full"
        src="@/assets/loading-v2-yellow.svg"
        alt=""
      />
      <p class="text-dark-gray">Loading payment details...</p>
    </div>
  </div>
</template>

<script>
import FormNavigationButtons from '@/components/FormNavigationButtons.vue';
import { useFormRouter } from '@/composables/formRouter';
import { useStore } from 'vuex';
import { computed, watch, ref, onBeforeMount, onMounted } from 'vue';
import RoundRadioButtonTerms from '../../RoundRadioButtonTerms.vue';

export default {
  name: 'PaymentStepOne',
  components: {
    'round-radio-button-terms': RoundRadioButtonTerms,
    FormNavigationButtons,
  },
  props: {
    data: Object,
    updateField: Function,
    formPosition: Number,
    title: String,
    nextStep: Function,
    stepName: String,
    skipStep: Function,
  },
  setup() {
    const store = useStore();
    const componentIdentifier = 'Payment-1';
    const formRouter = useFormRouter(componentIdentifier);
    const isNavigationDisabled = computed(() => formRouter.isDisabled.value);
    const isFactoringDeltaAvailable = computed(
      () =>
        store.state.payments?.payment_terms?.some(
          (term) => term?.is_type === 'delta'
        ) ?? false
    );
    const shouldEnableCHRFlow = computed(
      () =>
        isFactoringDeltaAvailable.value &&
        store.getters.shouldEnableCHRFlow &&
        !store.getters.isForeignCarrier
    );
    const firstTime = computed(
      () =>
        store.state.firstTime?.[
          formRouter.componentStep.value?.firstTimeIdentifier
        ]
    );
    const buttonsToDisplay = 'continue-save';
    const isButtonDisabled = false;
    const stepDirection = computed(() => store.state.formRoutes.stepDirection);
    const onboarding_id = computed(() => store.state.carriers?.carrierData?.id);
    const selectedPaymentTerm = computed(
      () => store.state.payments?.selected_payment_term
    );

    onBeforeMount(async () => {
      console.log('BeforeMount: Payment-1');
      console.log(onboarding_id.value);
      await store.dispatch(
        'payments/getActivePaymentMethods',
        onboarding_id.value
      );
    });

    onMounted(async () => {
      console.log('Mounted: Payment-1');
      await store.dispatch('factors/getLatestFactor');
    });

    const nextCheck1 = computed(
      () => store.state.payments?.payment_type == 'factoring'
    );
    const nextCheck2 = computed(
      () =>
        selectedPaymentTerm.value?.id > 0 &&
        selectedPaymentTerm.value?.id !== savedPaymentTerm.value?.id
    );

    const handleNext = async () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'handleNext' called"
      );
      if (isNavigationDisabled.value)
        return store.commit(
          'addFormDebugMessage',
          'ERROR: ' +
            componentIdentifier +
            ': formRouter is disabled, returning'
        );
      if (nextCheck1.value) {
        store.commit(
          'addFormDebugMessage',
          'NEXT CHECK 1 | ' +
            componentIdentifier +
            ': nextCheck1 is TRUE (payment_type is factoring), setting payment_type to factoring but not yet saving selected_payment_term as OP Term (factor_id required first), moving to Payment-2'
        );
        return formRouter.moveToNextStep();
      } else {
        store.commit(
          'addFormDebugMessage',
          'NEXT CHECK 1 | ' +
            componentIdentifier +
            ': nextCheck1 is FALSE (payment_type is not factoring)'
        );
      }
      if (nextCheck2.value) {
        store.commit(
          'addFormDebugMessage',
          'NEXT CHECK 2 | ' +
            componentIdentifier +
            ': nextCheck2 is TRUE (selected_payment_term present and different from saved_payment_term), removing saved_payment_term if present, saving selected_payment_term as OP Term, and moving to Payment-2'
        );
        const bodyN1 = {
          onboarding_packet: {
            payment_term_id: selectedPaymentTerm.value?.id,
          },
        };
        await store.dispatch('payments/nullifySavedPaymentMethod');
        await store.dispatch('onboardingPacket/updatePacket', {
          id: onboarding_id.value,
          body: bodyN1,
          canHitNextStep: false,
        });
        return formRouter.moveToNextStep();
      } else {
        store.commit(
          'addFormDebugMessage',
          'NEXT CHECK 2 | ' +
            componentIdentifier +
            ': nextCheck2 is FALSE (selected_payment_term not present or same as saved_payment_term), moving to Payment-2'
        );
      }
      formRouter.moveToNextStep();
    };

    // backChecks: Checks to be reviewed when the user clicks the Back button (but before any store.state.formRoutes.activeStep changes)
    const backCheck1 = computed(() => !store.state.firstTime['Insurance']);

    const handleBack = () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'handleBack' called"
      );
      if (isNavigationDisabled.value)
        return console.log(
          'ERROR: ' +
            componentIdentifier +
            ': formRouter is disabled, returning'
        );

      if (backCheck1.value) {
        store.commit(
          'addFormDebugMessage',
          'BACK CHECK 1 | ' +
            componentIdentifier +
            ': backCheck1 is TRUE (firstTime.Insurance is false), moving to Insurance-3'
        );
        return formRouter.moveToStepByIdentifier('Insurance-3');
      } else {
        store.commit(
          'addFormDebugMessage',
          'BACK CHECK 1 | ' +
            componentIdentifier +
            ': backCheck1 is FALSE (firstTime.Insurance is true)'
        );
      }

      formRouter.moveToPreviousStep();
    };

    // Determine whether to skip to Payment-2 if TriumphPay is rendered_payment
    // const isTriumphPayRendered = computed(() => {
    //   return store.state.payments.rendered_payment?.[0]?.is_type === 'triumph_pay' || store.state.payments.rendered_payment?.is_type === 'triumph_pay';
    // });
    const savedPaymentTerm = computed(
      () => store.state.payments?.saved_payment_term
    );
    // const isTriumphPayAllowedByPaymentTerms = computed(() => store.state.payments?.payment_terms?.some(term => term?.allowed_payment_methods?.includes('triumph_pay')));
    const brokerPaymentTerms = computed(() => [
      ...(store.state.payments?.payment_terms ?? []),
    ]);
    const brokerQuickPayTPayTermCount = computed(
      () =>
        brokerPaymentTerms.value?.filter(
          (term) =>
            term?.allowed_payment_methods?.includes('triumph_pay') &&
            term?.is_type == 'quick_pay' &&
            term?.is_active == true
        )?.length
    );
    const brokerQuickPayTPayTerm = computed(() =>
      brokerPaymentTerms.value?.find(
        (term) =>
          term?.allowed_payment_methods?.includes('triumph_pay') &&
          term?.is_type == 'quick_pay' &&
          term?.is_active == true
      )
    );
    const carrierAPMs = computed(() => [
      ...(store.state.payments?.activePaymentMethods ?? []),
    ]);
    const carrierHasSelectRateTPayMethod = computed(() =>
      carrierAPMs.value?.some(
        (method) =>
          method?.is_type == 'triumph_pay' &&
          method?.triumph_pay_select_carrier_rate != null &&
          Number(method?.triumph_pay_select_carrier_rate) > 0 &&
          method?.is_active == true
      )
    );
    const carrierClickedEditTPayMethod = computed(
      () => store.state.payments?.triumph_pay_declined
    );
    // const carrierAPMs = computed(() => {
    //   if (store.state.payments?.activePaymentMethods?.length == 0) {
    //     return store.state.carriers?.carrierData?.allowed_payment_methods;
    //   }
    // });

    // const carrierSelectRateTPayMethod = computed(() => carrierAPMs.value?.some(method => method?.is_type == 'triumph_pay'));
    // console.log('carrierSelectRateTPayMethod');
    // console.dir(carrierSelectRateTPayMethod.value);
    // console.log('store.state.payments?.activePaymentMethods');
    // console.dir(store.state.payments?.activePaymentMethods);

    const redirectCheckDataHasLoaded = computed(() => {
      return (
        store.state.payments?.paymentTermsHaveLoaded &&
        store.state.payments?.activePaymentMethodsHaveLoaded
      );
    });

    // (1) Carrier has not already selected a payment term other than the Broker's only Quick Pay TPay Term
    const rCheckHelper1 = computed(
      () =>
        savedPaymentTerm.value?.id > 0 == false ||
        (savedPaymentTerm.value?.id > 0 &&
          savedPaymentTerm.value?.id == brokerQuickPayTPayTerm.value?.id)
    );
    // (2) Carrier has a Select Rate TPay Method
    const rCheckHelper2 = computed(() => carrierHasSelectRateTPayMethod.value);
    // (3) Broker has exactly one Quick Pay TPay Term
    const rCheckHelper3 = computed(
      () =>
        brokerQuickPayTPayTermCount.value == 1 &&
        brokerQuickPayTPayTerm.value?.id > 0
    );
    // (4) Carrier has not clicked 'edit' when viewing the TPay Method within Payment-2
    const rCheckHelper4 = computed(() => !carrierClickedEditTPayMethod.value);

    const redirectCheck1 = computed(
      () => store.state.carriers?.carrierData?.auto_skip_stage_six
    );
    const redirectCheck2 = computed(
      () =>
        rCheckHelper1.value &&
        rCheckHelper2.value &&
        rCheckHelper3.value &&
        rCheckHelper4.value
    );

    const runRedirectCheck = async () => {
      // 1. auto_skip_stage_six Redirect Check
      store.commit(
        'addFormDebugMessage',
        'REDIRECT CHECK 1 | ' +
          componentIdentifier +
          ': Broker-Elected Skip of Payment Step'
      );
      store.commit(
        'addFormDebugMessage',
        'REDIRECT CHECK 1 | ' +
          componentIdentifier +
          ": Description: Will redirect if Broker's auto_skip_stage_six value is true"
      );
      if (redirectCheck1.value) {
        store.commit(
          'addFormDebugMessage',
          'REDIRECT CHECK 1 | ' + componentIdentifier + ': Redirect necessary'
        );
        return {
          redirect_check_number: 1,
          redirect_next_identifier: 'Questions',
          redirect_back_identifier: 'Insurance-4',
        };
      } else {
        store.commit(
          'addFormDebugMessage',
          'REDIRECT CHECK 1 | ' +
            componentIdentifier +
            ': No redirect necessary'
        );
      }

      // 2. TrimphPay Redirect Check
      store.commit(
        'addFormDebugMessage',
        'REDIRECT CHECK 2 | ' + componentIdentifier + ': Auto-Apply TPay'
      );
      console.log('carrierAPMs.value');
      console.dir(carrierAPMs.value);
      store.commit(
        'addFormDebugMessage',
        'REDIRECT CHECK 2 (' +
          redirectCheck2.value +
          ') | ' +
          componentIdentifier +
          ': Description: Will redirect if (1 - ' +
          rCheckHelper1.value +
          ") Carrier has not already selected a payment term other than the Broker's only Quick Pay TPay Term, (2 - " +
          rCheckHelper2.value +
          ') Carrier has a Select Rate TPay Method, (3 - ' +
          rCheckHelper3.value +
          ') Broker has exactly one Quick Pay TPay Term, AND (4 - ' +
          rCheckHelper4.value +
          ") Carrier has not clicked 'edit' when viewing the TPay Method within Payment-2"
      );
      // let renderedPayment = Array.isArray(store.state.payments.rendered_payment) ? store.state.payments.rendered_payment?.[0] : store.state.payments.rendered_payment;
      // if (renderedPayment?.triumph_pay_select_carrier_rate && renderedPayment?.is_type === 'triumph_pay') {
      //  if (Number(renderedPayment?.triumph_pay_select_carrier_rate) > 0 && isTriumphPayAllowedByPaymentTerms.value) {
      if (redirectCheck2.value) {
        store.commit(
          'addFormDebugMessage',
          'REDIRECT CHECK 2 | ' + componentIdentifier + ': Redirect necessary'
        );
        return {
          redirect_check_number: 2,
          redirect_next_identifier: 'Payment-2',
          redirect_back_identifier: 'Insurance-4',
        };
      } else {
        store.commit(
          'addFormDebugMessage',
          'REDIRECT CHECK 2 | ' +
            componentIdentifier +
            ': No redirect necessary'
        );
      }
      return false;
    };

    const redirectChecks = ref(null);

    watch(isNavigationDisabled, async (val1) => {
      if (val1 == false) {
        if (redirectCheckDataHasLoaded.value !== true) {
          store.commit(
            'addFormDebugMessage',
            'WAITING FOR REDIRECT CHECKS | ' +
              componentIdentifier +
              ': (isNavigationDisabled watcher) ' +
              componentIdentifier +
              ' is active step, but waiting for carrier active payment methods to load'
          );
        } else {
          store.commit(
            'addFormDebugMessage',
            'BEGIN REDIRECT CHECKS | ' +
              componentIdentifier +
              ': (isNavigationDisabled watcher) ' +
              componentIdentifier +
              ' is active step, running redirect checks'
          );
          redirectChecks.value = await runRedirectCheck();
          if (redirectChecks.value !== false) {
            handleRedirect(redirectChecks.value);
          }
        }
      }
    });

    watch(redirectCheckDataHasLoaded, async (val) => {
      if (val == true && isNavigationDisabled.value == false) {
        store.commit(
          'addFormDebugMessage',
          'BEGIN REDIRECT CHECKS | ' +
            componentIdentifier +
            ': (isNavigationDisabled watcher) ' +
            componentIdentifier +
            ' is active step, running redirect checks'
        );
        redirectChecks.value = await runRedirectCheck();
        if (redirectChecks.value !== false) {
          handleRedirect(redirectChecks.value);
        }
      }
    });
    // watch(redirectChecks, (val) => {
    //   if (val !== false) {
    //     console.log('redirectChecks watcher');
    //     console.dir(val);
    //     let d = new Date();
    //     console.log(d.getUTCMilliseconds());
    //   }
    // });

    const handleRedirect = async (redirect_check) => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'handleRedirect' called"
      );
      if (isNavigationDisabled.value)
        return store.commit(
          'addFormDebugMessage',
          'ERROR: ' +
            componentIdentifier +
            ': formRouter is disabled, returning'
        );
      let redirectIdentifier;
      console.log('stepDirection.value');
      console.log(stepDirection.value);
      if (stepDirection.value == 'back') {
        redirectIdentifier = redirect_check?.redirect_back_identifier;
      } else {
        redirectIdentifier = redirect_check?.redirect_next_identifier;
        if (redirect_check?.redirect_check_number == 2) {
          store.commit(
            'addFormDebugMessage',
            'AUTO-SUBMIT | REDIRECT CHECK ' +
              redirect_check?.redirect_check_number +
              ' | Forward | ' +
              componentIdentifier +
              ": Submitting Broker's Quick Pay TPay Term as OP Payment Term"
          );
          const bodyR2 = {
            onboarding_packet: {
              payment_term_id: brokerQuickPayTPayTerm.value?.id,
            },
          };
          await store.dispatch('onboardingPacket/updatePacket', {
            id: onboarding_id.value,
            body: bodyR2,
            canHitNextStep: false,
          });
        }
      }
      store.commit(
        'addFormDebugMessage',
        'REDIRECT CHECK ' +
          redirect_check?.redirect_check_number +
          ' | ' +
          componentIdentifier +
          ': Redirecting to ' +
          redirectIdentifier
      );
      formRouter.moveToStepByIdentifier(redirectIdentifier);
    };

    return {
      componentIdentifier,
      handleNext,
      handleBack,
      buttonsToDisplay,
      isButtonDisabled,
      isNavigationDisabled,
      firstTime,
      redirectChecks,
      brokerPaymentTerms,
      redirectCheckDataHasLoaded,
      carrierAPMs,
      isFactoringDeltaAvailable,
      shouldEnableCHRFlow,
    };
  },
  data() {
    return {
      isLoading: false,
      latest_triumph_pay_payment_method: null,
    };
  },
  // async mounted(){
  //   const onboardingPacketId = this.$route.params.id;
  //   this.$store.dispatch('payments/getActivePaymentMethods', onboardingPacketId)
  // },
  // async beforeCreate() {
  //   if (!(this.$store.state.payments?.activePaymentMethods?.length > 0)) {
  //     const onboardingPacketId = this.$route?.params?.id;
  //     await this.$store.dispatch('payments/getActivePaymentMethods', onboardingPacketId);
  //   }
  // },
  computed: {
    factoringDelta() {
      return (
        this.$store.state.payments?.payment_terms?.filter(
          (term) => term?.is_type === 'delta'
        )?.[0] || []
      );
    },
    onboardingPacketId() {
      return this.$store.state.onboardingPacket?.onboarding_id;
    },
    paymentTermId() {
      return this.$store.state.payments.payment_term_id;
    },
    payment_terms() {
      return this.$store.state.payments?.payment_terms || [];
    },
    sortedPaymentTerms() {
      return this.payment_terms.filter((term) => term?.is_type !== 'delta');
    },
    latest_allowed_payment_method() {
      return this.$store.state.payments?.latest_allowed_payment_method || null;
    },
    // latest_triumph_pay_payment_method() {
    //   return this.$store.state.payments?.latest_allowed_payment_method || null;
    // },
    selected_payment_term() {
      return this.$store.state.payments?.selected_payment_term || null;
    },
    paymentTermsDestructured() {
      return [...(this.$store.state.payments?.payment_terms ?? [])];
    },
    paymentTermsNonFactoring() {
      return this.paymentTermsDestructured?.filter(
        (term) => term?.is_type !== 'factoring'
      );
    },
  },
  watch: {
    stepName: function (val) {
      if (val === 'Payment-1') {
        console.log('running stepName watcher for Payment-1');
        this.getPaymentTerms(this.onboardingPacketId);
        this.getLatestTriumphPayPaymentMethod();
        this.checkFactoringDelta();

        // this.getLatestTriumphPayPaymentMethod().then((res) => {
        //   if (!this.data?.payment_method && res?.data?.[0]?.triumph_pay_select_carrier_rate > 0 && this.isPaymentMethodAllowed('triumph_pay')) {
        //     let payload = {
        //       body: {
        //         "onboarding_packet": {
        //           "payment_method_id": this.latest_triumph_pay_payment_method?.id
        //         }
        //       }
        //     };
        //     if (this.latest_allowed_payment_method?.is_type == 'triumph_pay') {
        //       console.log('Updating OnboardingPacket from Payment-1')
        //       this.$store.commit('addFormDebugMessage', "AUTO PACKET UPDATE | Payment-1: (stepName watcher) Updating payment_method_id to " + this.latest_triumph_pay_payment_method?.id);
        //       this.$store.dispatch("onboardingPacket/updatePacket", payload);
        //       this.$store.commit("firstTime/updateStepFirstTime", {
        //         step: 'Payment',
        //         value: false,
        //       });
        //     }
        //   }
        // });

        // if there is no payment method query the payment method, then set the payment method to the user.
        if (this.selected_payment_term?.id > 0) {
          this.$store.commit('toggleDisabledButton', {
            bool: false,
            source: 'PaymentStepOne #2',
          });
        } else {
          this.$store.commit('toggleDisabledButton', {
            bool: true,
            source: 'PaymentStepOne #3',
          });
        }
      }
    },
    selected_payment_term(val) {
      if (this.stepName === 'Payment-1') {
        if (val?.id > 0) {
          this.$store.commit('toggleDisabledButton', {
            bool: false,
            source: 'PaymentStepOne #4',
          });
        } else {
          this.$store.commit('toggleDisabledButton', {
            bool: true,
            source: 'PaymentStepOne #5',
          });
        }
      }
    },
  },
  methods: {
    checkFactoringDelta() {
      // This method needs to check if Factoring Delta is available and if so, set the payment term to Factoring Delta automatically
      if (!this.shouldEnableCHRFlow) return;
      if (!this.factoringDelta) return;
      // this.handleClick(this.factoringDelta);
    },
    approvedPaymentMethods(apm, type) {
      if (type === 'factoring') return {};

      return apm;
    },
    // isPaymentMethodAllowed(requestedPaymentMethod) {
    //   const paymentTerms = this.$store.state.payments?.payment_terms;
    //   return paymentTerms?.some(payment => payment?.allowed_payment_methods?.includes(requestedPaymentMethod));
    // },
    isPaymentMethodAllowed(requestedPaymentMethod) {
      return this.paymentTermsNonFactoring?.some((term) =>
        term?.allowed_payment_methods?.includes(requestedPaymentMethod)
      );
    },
    async handleClick(payment_term) {
      // console.log('payment_term');
      // console.dir(payment_term);
      this.$store.commit('payments/setSelectedPaymentTerm', payment_term);
      this.$store.dispatch('payments/updateNestedField', {
        path: 'payment_term_id',
        value: payment_term?.id,
      });
      this.$store.dispatch('payments/updateNestedField', {
        path: 'payment_type',
        value: payment_term?.is_type,
      });
      let onBoardingPacket = this.createOnboardingPacket(payment_term);
      this.getLatestAllowedPaymentMethod(payment_term);
      this.$store.commit('toggleDisabledButton', {
        bool: false,
        source: 'PaymentStepOne #1',
      });

      if (payment_term?.is_type !== 'factoring') {
        this.updateOnboardingPacket(onBoardingPacket);
      } else {
        this.$store.dispatch('onboardingPacket/setOnboardingBody', {});
        await this.$store.dispatch('factors/getLatestFactor');
      }
    },
    async getPaymentTerms(id) {
      let res = await this.$store.dispatch('payments/getPaymentTerms', id);

      if ([200, 201].includes(res?.status)) {
        // this.payment_terms = res?.data;
        this.isLoading = false;
      } else {
        this.isLoading = false;
      }
    },
    async getLatestAllowedPaymentMethod(payment_term) {
      await this.$store.dispatch(
        'payments/getLatestAllowedPaymentMethod',
        payment_term
      );
      // if ([200, 201].includes(res?.status)) {
      //   this.latest_allowed_payment_method = res?.data;
      // }
    },
    async getLatestTriumphPayPaymentMethod() {
      // await this.$store.dispatch("payments/getLatestTriumphPayPaymentMethod");
      let res = await this.$store.dispatch(
        'payments/getLatestTriumphPayPaymentMethod'
      );
      if ([200, 201].includes(res?.status)) {
        this.latest_triumph_pay_payment_method = res?.data?.[0];
      }
    },
    feesToText(paymentTerm) {
      if (!paymentTerm) return;
      let type = paymentTerm?.is_type;
      if (type === 'factoring') return;
      let discountRate = paymentTerm?.discount_rate * 100;
      if (discountRate?.toString()?.length > 4) {
        discountRate = discountRate?.toFixed(2) + '%';
      } else {
        discountRate = discountRate + '%';
      }

      let daysToPay = parseInt(paymentTerm?.days_to_pay);
      return (
        daysToPay +
        (daysToPay == 1 ? ' Day, ' : ' Days, ') +
        (paymentTerm?.discount_rate ? discountRate : 'No') +
        ' Fee'
      );
    },
    createOnboardingPacket(payment_term) {
      const { saved_payment_term, saved_payment_method, activePaymentMethods } =
        this.$store.state.payments;

      const allowed_pm = payment_term?.allowed_payment_methods ?? [];

      const allowedMethods =
        allowed_pm.length > 0
          ? allowed_pm
          : payment_term?.is_type !== 'factoring'
            ? ['ach']
            : [];

      const isDifferentTerm = saved_payment_term?.id !== payment_term?.id;
      const isSavedMethodValid = allowedMethods.includes(
        saved_payment_method?.is_type
      );

      if (saved_payment_term && isDifferentTerm && isSavedMethodValid) {
        const result = {
          payment_term_id: payment_term.id,
          payment_method_id: saved_payment_method.id,
          factor_id: null,
        };
        return result;
      }

      if (saved_payment_term && isDifferentTerm && !isSavedMethodValid) {
        const validMethods = (activePaymentMethods ?? []).filter((pm) =>
          allowedMethods.includes(pm?.is_type)
        );

        const result = {
          payment_term_id: payment_term.id,
          payment_method_id: validMethods[0]?.id ?? null,
          factor_id: null,
        };

        return result;
      }

      const fallbackResult = {
        payment_term_id: payment_term.id,
        payment_method_id: null,
        factor_id: null,
      };

      console.log(
        'AMSPAYMENT - Returning fallback/default result:',
        fallbackResult
      );
      return fallbackResult;
    },
    updateOnboardingPacket(body) {
      this.$store.dispatch('onboardingPacket/setOnboardingBody', {
        onboarding_packet: body,
      });
    },
  },
};
</script>
